import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { type Event } from '../lib/api/events';
import { supabase } from '../lib/supabase';
import { bookingNotificationService } from '@/services/bookingNotificationService';
import { getTimezoneFromCoordinates, convertLocalTimeToUTC } from '@/utils/timezoneUtils';

interface TimeSlot {
  slotname: string;
  isdisabled: boolean;
}

const getAvailableTimeSlots = async (date: Date, lat: number, lon: number): Promise<TimeSlot[]> => {
  const year = date.getFullYear();
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');

  const apiURL = `https://mysites.ensiteservices.com/api/ensite/getslotforsurvey?creatorparentid=1234&date=${year}-${month}-${day}&latitude=${lat}&longitude=${lon}&issurveycreating=true&clientid=7125`;

  try {
    const jwt = await fetch("https://mysites.ensiteservices.com/api/auth/get-token", {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'        
      },
      body: JSON.stringify({
        "identifier": "tech@solerro.com",
        "password": "Solerro1!_Tech"
      })
    });
    const jwtData = await jwt.json();
    const jwtToken = jwtData.jwt;
    
    const response = await fetch(apiURL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwtToken}`
      },
    });

    if (!response.ok) throw new Error("Network response was not ok");

    const data: TimeSlot[] = await response.json();
    
    const currentDate = new Date();
    const currentUTCHours = currentDate.getUTCHours();
    // Calculate the difference in days
    const differenceInDays = (date.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24);

    if (currentUTCHours >= 2 && currentUTCHours <= 19 && (differenceInDays < 1)) {
      for (let i = 0; i < data.length; i++) {
        if (data[i].slotname === '08' || data[i].slotname === '10') {
          data[i].isdisabled = true;
        }
      }
    }

    return data;
  } catch (error) {
    console.error("Fetch failed:", error);
    throw error;
  }
};

const getEnsiteStatus = async (postalCode: string): Promise<string> => {
  try {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      "zip_code": postalCode
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow" as RequestRedirect
    };

    const response = await fetch("https://cqynudrcrwlodsxuowbm.supabase.co/functions/v1/get_ensite_status", requestOptions);
    
    if (!response.ok) {
      throw new Error("Failed to get Ensite status");
    }
    
    const result = await response.json();
    return result.ci_level || "good"; // Default to "good" if no ci_level is returned
  } catch (error) {
    console.error("Error fetching Ensite status:", error);
    return "good"; // Default to "good" if there's an error
  }
};

const bookingFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  phoneNumber: z.string().min(1, 'Phone number is required'),
  salesRepName: z.string().min(1, 'Sales representative name is required'),
  selectedDate: z.string().min(1, 'Please select a date'),
  selectedTime: z.string().min(1, 'Please select a time'),
  // Property details
  levels: z.enum(['One', 'Two', 'Three'], {
    required_error: 'Please select number of levels',
  }),
  atticAccess: z.enum(['In Garage', 'In Bedroom', 'In Hallway'], {
    required_error: 'Please select attic access location',
  }),
  mountType: z.enum(['Roof Mount', 'Ground Mount', 'Both'], {
    required_error: 'Please select mount type',
  }),
  metersOnSite: z.enum(['One', 'Two', 'Three'], {
    required_error: 'Please select number of meters',
  }),
  electricalPanels: z.enum(['One', 'Two', 'Three'], {
    required_error: 'Please select number of electrical panels',
  }),
  animals: z.enum(['Dogs', 'Cats', 'None'], {
    required_error: 'Please select animals present',
  }),
  hasBattery: z.enum(['Yes', 'No'], {
    required_error: 'Please specify if the project has a battery',
  }),
  requiresConstruction: z.enum(['Yes', 'No'], {
    required_error: 'Please specify if construction is required',
  }),
  constructionDetails: z.string().optional(),
  installerNote: z.string().optional(),
  // Address fields
  address: z.string().min(1, 'Address is required'),
  streetAddress: z.string().min(1, 'Street address is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  postalCode: z.string().min(1, 'Postal code is required'),
  country: z.string().min(1, 'Country is required'),
  latitude: z.number(),
  longitude: z.number(),
  siteCoverage: z.string().optional(),
});

type BookingFormData = z.infer<typeof bookingFormSchema>;

interface BookingFormEnsiteProps {
  event: Event;
  onSubmit?: (data: BookingFormData) => void;
  initialAddress?: {
    address: string;
    street_address: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    latitude: number;
    longitude: number;
  };
  urlParams?: URLSearchParams;
  projectId?: string; // Added optional projectId
}

function BookingFormEnsite({ event, onSubmit, initialAddress, urlParams, projectId }: BookingFormEnsiteProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<{ start: string; end: string }[]>([]);
  const [ciLevel, setCiLevel] = useState<string>("loading");
  const [isLoadingCiLevel, setIsLoadingCiLevel] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [customerTimezone, setCustomerTimezone] = useState<{
    timeZoneId: string;
    timeZoneName: string;
    rawOffset: number;
    dstOffset: number;
  } | null>(null);
  
  // Get default values from URL parameters
  const getDefaultValue = (key: string, defaultVal: string = '') => {
    return urlParams?.get(key) || defaultVal;
  };
  
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<BookingFormData>({
    resolver: zodResolver(bookingFormSchema),
    defaultValues: {
      // Set default values for address fields from initialAddress
      address: initialAddress?.address || getDefaultValue('address', ''),
      streetAddress: initialAddress?.street_address || getDefaultValue('streetAddress', ''),
      city: initialAddress?.city || getDefaultValue('city', ''),
      state: initialAddress?.state || getDefaultValue('state', ''),
      postalCode: initialAddress?.postal_code || getDefaultValue('postalCode', ''),
      country: initialAddress?.country || getDefaultValue('country', ''),
      latitude: initialAddress?.latitude || Number(getDefaultValue('latitude', '0')),
      longitude: initialAddress?.longitude || Number(getDefaultValue('longitude', '0')),
      siteCoverage: getDefaultValue('siteCoverage', ''),
      // Form fields from URL parameters
      firstName: getDefaultValue('firstName', ''),
      lastName: getDefaultValue('lastName', ''),
      email: getDefaultValue('email', ''),
      phoneNumber: getDefaultValue('phoneNumber', ''),
      salesRepName: getDefaultValue('salesRepName', ''),
      levels: getDefaultValue('levels', '') as 'One' | 'Two' | 'Three',
      atticAccess: getDefaultValue('atticAccess', '') as 'In Garage' | 'In Bedroom' | 'In Hallway',
      mountType: getDefaultValue('mountType', '') as 'Roof Mount' | 'Ground Mount' | 'Both',
      metersOnSite: getDefaultValue('metersOnSite', '') as 'One' | 'Two' | 'Three',
      electricalPanels: getDefaultValue('electricalPanels', '') as 'One' | 'Two' | 'Three',
      animals: getDefaultValue('animals', '') as 'Dogs' | 'Cats' | 'None',
      hasBattery: getDefaultValue('hasBattery', 'No') as 'Yes' | 'No',
      requiresConstruction: getDefaultValue('requiresConstruction', 'No') as 'Yes' | 'No',
      constructionDetails: getDefaultValue('constructionDetails', ''),
      installerNote: getDefaultValue('installerNote', ''),
      selectedDate: getDefaultValue('selectedDate', ''),
      selectedTime: getDefaultValue('selectedTime', ''),
    },
  });

  const requiresConstruction = watch('requiresConstruction');

  useEffect(() => {
    // Fetch CI level when component mounts
    const fetchCiLevel = async () => {
      if (initialAddress?.postal_code) {
        setIsLoadingCiLevel(true);
        try {
          const level = await getEnsiteStatus(initialAddress.postal_code);
          // Normalize the CI level to lowercase for consistent comparison
          setCiLevel(level.toLowerCase());
          setValue('siteCoverage', level);
        } catch (error) {
          console.error("Error fetching CI level:", error);
          setCiLevel("lite"); // Default to "lite" if there's an error
          setValue('siteCoverage', "lite");
        } finally {
          setIsLoadingCiLevel(false);
        }
      } else {
        setCiLevel("lite"); // Default to "lite" if no postal code
        setValue('siteCoverage', "lite");
        setIsLoadingCiLevel(false);
      }
    };

    fetchCiLevel();
  }, [initialAddress, setValue]);

  useEffect(() => {
    // Generate dates based on CI level
    // if (isLoadingCiLevel) return;

    const generateDates = () => {
      if (isLoadingCiLevel) return;
      
      const dates: string[] = [];
      const today = new Date();
      let startDay = 5; // Default start day
      
      // Determine start day based on CI level
      // Normalize all comparisons to lowercase
      const normalizedCiLevel = ciLevel.toLowerCase();
      console.log("normalizedCiLevel", normalizedCiLevel, ciLevel)
      if (normalizedCiLevel === "lite" || normalizedCiLevel === "light") {
        startDay = 6; // Start from 5 days after today for lite coverage
      } else if (normalizedCiLevel === "good") {
        startDay = 4; // Start from 3 days after today for good coverage
      } else if (normalizedCiLevel === "strong") {
        startDay = 2; // Start from 2 days after today for strong coverage
      } else {
        startDay = 5; // Default to 3 days if unknown level
      }
      
      console.log(`CI Level: ${normalizedCiLevel}, Start Day: ${startDay}`);
      
      // Generate dates for the next 30 days starting from the determined start day
      let daysAdded = 0;
      let currentDay = 0;
      
      while (daysAdded < 30) {
        const date = new Date();
        date.setDate(today.getDate() + currentDay);
        
        // Skip Sundays
        if (date.getUTCDay()!=0) {
          // Only add dates starting from the appropriate start day\
          console.log("current day", currentDay)
          if (currentDay >= startDay) {
            // Format date as YYYY-MM-DD
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');
            dates.push(`${year}-${month}-${day}`);
            daysAdded++;
          }
        }
        
        currentDay++;
      }
      console.log(availableDates)
      setAvailableDates(dates);
      
      // Automatically select the first available date if none is already selected
      if (dates.length > 0 && !selectedDate) {
        setValue('selectedDate', dates[0]);
        setSelectedDate(dates[0]);
      }
    };

    generateDates();
  }, [ciLevel, isLoadingCiLevel, selectedDate, setValue]);

  useEffect(() => {
    // Keep the form's selectedDate value in sync with our selectedDate state
    const subscription = watch((value, { name }) => {
      if (name === 'selectedDate' && value.selectedDate) {
        setSelectedDate(value.selectedDate as string);
      }
    });
    
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    const fetchTimeSlots = async () => {
      if (!selectedDate || !initialAddress) return;
      
      console.log("Fetching time slots for:", selectedDate, "at address:", initialAddress);
      
      try {
        setIsLoadingTimeSlots(true);
        // Reset selected time when date changes
        setValue('selectedTime', '');
        setAvailableTimeSlots([]);
        
        const slots = await getAvailableTimeSlots(
          new Date(selectedDate), 
          initialAddress?.latitude || 0, 
          initialAddress?.longitude || 0
        );
        
        const formattedSlots = slots.map(slot => {
          const startHour = parseInt(slot.slotname);
          const endHour = startHour + 2;
          
          // Create clean time strings without Date objects
          const startTime = `${startHour}:00`;
          const endTime = `${endHour}:00`;
          
          // Format to 12-hour format
          const formatTime = (hour: number) => {
            const period = hour >= 12 ? 'PM' : 'AM';
            const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
            return `${displayHour}:00 ${period}`;
          };
          
          return {
            start: formatTime(startHour),
            end: formatTime(endHour)
          };
        });
        
        console.log('Formatted time slots:', formattedSlots);
        setAvailableTimeSlots(formattedSlots);
      } catch (error) {
        console.error('Error fetching time slots:', error);
        toast.error('Failed to load available time slots');
      } finally {
        setIsLoadingTimeSlots(false);
      }
    };

    fetchTimeSlots();
  }, [selectedDate, initialAddress, setValue]);

  // Fetch customer timezone when address coordinates are available
  useEffect(() => {
    const fetchCustomerTimezone = async () => {
      if (initialAddress?.latitude && initialAddress?.longitude) {
        try {
          const timezone = await getTimezoneFromCoordinates(initialAddress.latitude, initialAddress.longitude);
          setCustomerTimezone(timezone);
          console.log('Customer timezone set (Ensite):', timezone);
        } catch (error) {
          console.error('Error fetching customer timezone:', error);
          // Fallback to user's timezone
          setCustomerTimezone({
            timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
            timeZoneName: 'User Timezone (fallback)',
            rawOffset: 0,
            dstOffset: 0
          });
        }
      }
    };

    fetchCustomerTimezone();
  }, [initialAddress?.latitude, initialAddress?.longitude]);

  const onFormSubmit = async (data: BookingFormData) => {
    try {
      console.log("Form submission started", data);
      setIsLoading(true);
      
      if (onSubmit) {
        console.log("Using provided onSubmit handler");
        await onSubmit(data);
      } else {
        console.log("Using default form submission logic");
        
        // Use stored customer timezone or fallback to user's timezone
        const timezoneToUse = customerTimezone || {
          timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
          timeZoneName: 'User Timezone (fallback)',
          rawOffset: 0,
          dstOffset: 0
        };
        
        console.log('Using timezone for booking (Ensite):', timezoneToUse);
        
        // Parse the date and time
        const [year, month, day] = data.selectedDate.split('-').map(Number);
        const timeMatch = data.selectedTime.match(/(\d+):(\d+)\s+([AP]M)/i);
        if (!timeMatch) {
          throw new Error('Invalid time format');
        }
        
        const [hours, minutes, period] = timeMatch.slice(1);
        let hour = parseInt(hours);
        if (period.toUpperCase() === 'PM' && hour < 12) hour += 12;
        if (period.toUpperCase() === 'AM' && hour === 12) hour = 0;
        
        // Create the date in the customer's timezone (project address timezone)
        const customerDate = new Date(year, month - 1, day, hour, parseInt(minutes));
        
        // Convert the customer's local time to UTC for storage
        // This is the correct approach: customer's local time -> UTC
        const startTime = convertLocalTimeToUTC(customerDate, timezoneToUse.timeZoneId);
        
        const endTime = new Date(startTime);
        endTime.setMinutes(endTime.getMinutes() + event.duration);

        console.log("Preparing booking data with customer timezone", { 
          startTime: startTime.toISOString(), 
          endTime: endTime.toISOString(),
          customerTimezone: timezoneToUse.timeZoneId,
          customerStartTimeLocal: customerDate.toString(),
          customerEndTimeLocal: new Date(customerDate.getTime() + event.duration * 60000).toString()
        });

        const extraInformation = {
          levels: data.levels,
          atticAccess: data.atticAccess,
          mountType: data.mountType,
          metersOnSite: data.metersOnSite,
          electricalPanels: data.electricalPanels,
          animals: data.animals,
          hasBattery: data.hasBattery,
          requiresConstruction: data.requiresConstruction,
          constructionDetails: data.constructionDetails,
          installerNote: data.installerNote,
          siteCoverage: data.siteCoverage,
          address: {
            full: data.address,
            street: data.streetAddress,
            city: data.city,
            state: data.state,
            postalCode: data.postalCode,
            country: data.country,
            coordinates: {
              latitude: data.latitude,
              longitude: data.longitude,
            },
          },
          // Add timezone information
          webhookData: {
            start_time: startTime.toISOString(),
            end_time: endTime.toISOString(),
            local_start_time: customerDate.toISOString(),
            local_end_time: new Date(customerDate.getTime() + event.duration * 60000).toISOString(),
            timezone: timezoneToUse.timeZoneId,
            timezone_friendly: timezoneToUse.timeZoneName,
            formatted_date: format(customerDate, 'MMMM d, yyyy'),
            formatted_time: format(customerDate, 'h:mm a'),
            formatted_datetime: format(customerDate, 'MMMM d, yyyy h:mm a')
          }
        };

        console.log("Submitting to Supabase", { extraInformation });

        // Fetch the project's tenant_id
        let tenantId = null;
        if (projectId) {
          const { data: projectData, error: projectError } = await supabase
            .from('projects')
            .select('tenant_id')
            .eq('id', projectId)
            .single();
          
          if (projectError) {
            console.error('Error fetching project tenant_id:', projectError);
            throw new Error('Failed to fetch project information');
          }
          
          tenantId = projectData?.tenant_id;
        }

        const { data: bookingResult, error: bookingError } = await supabase
          .from('bookings')
          .insert({
            event_id: event.id,
            project_id: projectId,
            tenant_id: tenantId,
            first_name: data.firstName,
            last_name: data.lastName,
            email: data.email,
            phone: data.phoneNumber,
            sales_rep_name: data.salesRepName,
            start_time: startTime.toISOString(),
            end_time: endTime.toISOString(),
            extra_information: extraInformation,
          })
          .select()
          .single();

        if (bookingError) throw bookingError;

        // Send booking notifications and capture email status
        let emailStatus: { emailSent: boolean; emailError?: string } = { emailSent: false, emailError: 'Notifications not sent' };
        if (bookingResult) {
          try {
            emailStatus = await bookingNotificationService.notifyBookingCreated({
              bookingId: bookingResult.id,
              projectId: projectId,
              customerName: `${data.firstName} ${data.lastName}`,
              customerEmail: data.email,
              salesRepName: data.salesRepName,
              bookingDate: data.selectedDate,
              bookingTime: data.selectedTime,
              address: data.address,
              eventName: event.name,
              eventType: event.meeting_type || 'Site Survey',
              customerTimezone: timezoneToUse.timeZoneId,
              metadata: {
                phoneNumber: data.phoneNumber
              }
            });
          } catch (notificationError) {
            console.error('Error sending booking notifications:', notificationError);
            emailStatus = { emailSent: false, emailError: 'Notification service error' };
          }
        }

        toast.success('Booking submitted successfully');

        // Get sales rep email if project exists
        let salesRepEmail = null;
        if (projectId) {
          try {
            const { data: project } = await supabase
              .from('projects')
              .select(`
                sales_rep:users!assigned_sales_rep_id (
                  email,
                  first_name,
                  last_name
                )
              `)
              .eq('id', projectId)
              .single() as { data: { sales_rep: { email: string; first_name: string; last_name: string } | null } | null; error: any };

            salesRepEmail = project?.sales_rep?.email;
          } catch (error) {
            console.warn('Could not fetch sales rep email for confirmation message:', error);
          }
        }

        // Navigate to confirmation page with booking details
        navigate('/app/booking-confirmation', {
          state: {
            bookingDetails: {
              firstName: data.firstName,
              lastName: data.lastName,
              email: data.email,
              phoneNumber: data.phoneNumber,
              startTime: startTime.toISOString(),
              endTime: endTime.toISOString(),
              eventName: event.name,
              meetingType: event.meeting_type,
              meetingLink: event.meeting_link,
              address: data.address,
              extraInformation: extraInformation,
              // Add customer timezone information
              customerTimezone: timezoneToUse.timeZoneId,
              customerTimezoneName: timezoneToUse.timeZoneName,
              customerStartTime: customerDate.toString(),
              customerEndTime: new Date(customerDate.getTime() + event.duration * 60000).toString(),
              // Add sales rep info for confirmation message
              salesRepEmail: salesRepEmail,
              confirmationEmailSentTo: salesRepEmail || data.email,
              // Add email status information
              emailSent: emailStatus.emailSent,
              emailError: emailStatus.emailError
            },
          },
        });
      }
    } catch (error) {
      console.error('Booking submission failed:', error);
      toast.error('Failed to submit booking');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to get a human-readable description of the CI level
  const getCiLevelDescription = (level: string) => {
    const normalizedLevel = level.toLowerCase();
    switch (normalizedLevel) {
      case "lite":
      case "light":
        return "Light Coverage (5+ days out)";
      case "good":
        return "Good Coverage (3+ days out)";
      case "strong":
        return "Strong Coverage (2+ days out)";
      default:
        return "Light Coverage (5+ days out)";
    }
  };

  const [progress, setProgress] = useState(0);
  const intervalId = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isLoadingTimeSlots) {
      // Reset progress when loading starts
      setProgress(0);
      
      // Create a non-linear progress that moves faster at first and slows down
      // This creates a more realistic feeling of progress
      intervalId.current = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress < 30) {
            // Move quickly to 30%
            return prevProgress + 2;
          } else if (prevProgress < 70) {
            // Slow down in the middle
            return prevProgress + 1;
          } else if (prevProgress < 90) {
            // Even slower near the end
            return prevProgress + 0.5;
          } else if (prevProgress < 98) {
            // Almost stopped at the end
            return prevProgress + 0.1;
          }
          return 98; // Never reach 100% until actually loaded
        });
      }, 300);
    } else {
      // When loading completes, quickly fill to 100% and clear interval
      setProgress(100);
      setTimeout(() => {
        clearInterval(intervalId.current!);
      }, 300);
    }
    
    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
    };
  }, [isLoadingTimeSlots]);

  return (
    <form onSubmit={handleSubmit((data) => {
      console.log("Form validation passed", data);
      onFormSubmit(data);
    }, (errors) => {
      console.error("Form validation failed", errors);
      Object.keys(errors).forEach(key => {
        // Type-safe way to access error messages
        const errorMessage = Object.entries(errors)
          .find(([k]) => k === key)?.[1]?.message;
        toast.error(`${key}: ${errorMessage || 'Invalid input'}`);
      });
    })} className="space-y-8">
      {/* Combined Installation Address and Site Coverage section */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex flex-col space-y-4">
          {/* Installation Address */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-2">Installation Address</h3>
            <p className="text-foreground font-medium">{watch('address')}</p>
            
            {/* Hidden address fields to maintain form state */}
            <input type="hidden" {...register('streetAddress')} />
            <input type="hidden" {...register('city')} />
            <input type="hidden" {...register('state')} />
            <input type="hidden" {...register('postalCode')} />
            <input type="hidden" {...register('country')} />
            <input type="hidden" {...register('latitude')} />
            <input type="hidden" {...register('longitude')} />
          </div>
          
          {/* Site Coverage */}
          {isLoadingCiLevel ? (
            <div className="flex items-center mt-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mr-3"></div>
              <p className="text-gray-600">Loading site coverage information...</p>
            </div>
          ) : (
            <div className="mt-4">
              <div className="flex items-center">
                <div className={`h-3 w-3 rounded-full mr-2 ${
                  ciLevel.toLowerCase() === 'strong' ? 'bg-success/50' : 
                  ciLevel.toLowerCase() === 'good' ? 'bg-primary/50' : 
                  'bg-destructive/50'
                }`}></div>
                <p className="text-sm text-foreground">
                  {getCiLevelDescription(ciLevel)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Schedule Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
        <h3 className="text-lg font-medium text-foreground">Schedule</h3>
        <div className="space-y-6">
          <div>
            <label htmlFor="selectedDate" className="block text-sm font-medium text-foreground">
              Select Date
            </label>
            <select
              id="selectedDate"
              {...register('selectedDate')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
              disabled={isLoadingCiLevel}
            >
              {availableDates.map((date) => (
                <option key={date} value={date}>
                  {format(parseISO(date), 'EEEE, MMMM d, yyyy')}
                </option>
              ))}
            </select>
            {errors.selectedDate && (
              <p className="mt-1 text-sm text-destructive">{errors.selectedDate.message}</p>
            )}
          </div>

          {selectedDate && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Select Time
              </label>
              {isLoadingTimeSlots ? (
                <div className="text-center py-4">
                  <div className="bg-muted rounded-full h-3 w-full overflow-hidden shadow-inner">
                    <div 
                      className="bg-primary h-3 rounded-full transition-all duration-300 ease-out" 
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">Loading available time slots...</p>
                </div>
              ) : availableTimeSlots.length > 0 ? (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {availableTimeSlots.map((slot, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setValue('selectedTime', slot.start)}
                      className={`w-full py-3 rounded-xl border transition-colors text-base font-medium flex items-center justify-center
                        ${watch('selectedTime') === slot.start
                          ? 'bg-primary text-white hover:bg-primaryHover'
                          : 'bg-white text-foreground border border-border hover:bg-gray-50'
                        }
                        focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
                    >
                      {slot.start} - {slot.end}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No time slots available for this date. Please select another date.
                </p>
              )}
              {errors.selectedTime && (
                <p className="mt-2 text-sm text-destructive">{errors.selectedTime.message}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Contact Information Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
        <h3 className="text-lg font-medium text-foreground">Contact Information</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-foreground">
              First Name
            </label>
            <input
              type="text"
              id="firstName"
              {...register('firstName')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
            />
            {errors.firstName && (
              <p className="mt-1 text-sm text-destructive">{errors.firstName.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-foreground">
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              {...register('lastName')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
            />
            {errors.lastName && (
              <p className="mt-1 text-sm text-destructive">{errors.lastName.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground">
              Email
            </label>
            <input
              type="email"
              id="email"
              {...register('email')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="phoneNumber" className="block text-sm font-medium text-foreground">
              Phone Number
            </label>
            <input
              type="tel"
              id="phoneNumber"
              {...register('phoneNumber')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
            />
            {errors.phoneNumber && (
              <p className="mt-1 text-sm text-destructive">{errors.phoneNumber.message}</p>
            )}
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="salesRepName" className="block text-sm font-medium text-foreground">
              Sales Representative Name
            </label>
            <input
              type="text"
              id="salesRepName"
              {...register('salesRepName')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
            />
            {errors.salesRepName && (
              <p className="mt-1 text-sm text-destructive">{errors.salesRepName.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Property Details Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
        <h3 className="text-lg font-medium text-foreground">Property Details</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Number of Levels
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="levels-one"
                  type="radio"
                  value="One"
                  {...register('levels')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="levels-one" className="ml-3 block text-sm font-medium text-foreground">
                  One
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="levels-two"
                  type="radio"
                  value="Two"
                  {...register('levels')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="levels-two" className="ml-3 block text-sm font-medium text-foreground">
                  Two
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="levels-three"
                  type="radio"
                  value="Three"
                  {...register('levels')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="levels-three" className="ml-3 block text-sm font-medium text-foreground">
                  Three
                </label>
              </div>
            </div>
            {errors.levels && (
              <p className="mt-1 text-sm text-destructive">{errors.levels.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Attic Access Location
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="attic-garage"
                  type="radio"
                  value="In Garage"
                  {...register('atticAccess')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="attic-garage" className="ml-3 block text-sm font-medium text-foreground">
                  In Garage
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="attic-bedroom"
                  type="radio"
                  value="In Bedroom"
                  {...register('atticAccess')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="attic-bedroom" className="ml-3 block text-sm font-medium text-foreground">
                  In Bedroom
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="attic-hallway"
                  type="radio"
                  value="In Hallway"
                  {...register('atticAccess')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="attic-hallway" className="ml-3 block text-sm font-medium text-foreground">
                  In Hallway
                </label>
              </div>
            </div>
            {errors.atticAccess && (
              <p className="mt-1 text-sm text-destructive">{errors.atticAccess.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Type of Mount
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="mount-roof"
                  type="radio"
                  value="Roof Mount"
                  {...register('mountType')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="mount-roof" className="ml-3 block text-sm font-medium text-foreground">
                  Roof Mount
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="mount-ground"
                  type="radio"
                  value="Ground Mount"
                  {...register('mountType')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="mount-ground" className="ml-3 block text-sm font-medium text-foreground">
                  Ground Mount
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="mount-both"
                  type="radio"
                  value="Both"
                  {...register('mountType')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="mount-both" className="ml-3 block text-sm font-medium text-foreground">
                  Both
                </label>
              </div>
            </div>
            {errors.mountType && (
              <p className="mt-1 text-sm text-destructive">{errors.mountType.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Meters on Site
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="meters-one"
                  type="radio"
                  value="One"
                  {...register('metersOnSite')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="meters-one" className="ml-3 block text-sm font-medium text-foreground">
                  One
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="meters-two"
                  type="radio"
                  value="Two"
                  {...register('metersOnSite')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="meters-two" className="ml-3 block text-sm font-medium text-foreground">
                  Two
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="meters-three"
                  type="radio"
                  value="Three"
                  {...register('metersOnSite')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="meters-three" className="ml-3 block text-sm font-medium text-foreground">
                  Three
                </label>
              </div>
            </div>
            {errors.metersOnSite && (
              <p className="mt-1 text-sm text-destructive">{errors.metersOnSite.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Electrical Panels on Site
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="panels-one"
                  type="radio"
                  value="One"
                  {...register('electricalPanels')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="panels-one" className="ml-3 block text-sm font-medium text-foreground">
                  One
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="panels-two"
                  type="radio"
                  value="Two"
                  {...register('electricalPanels')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="panels-two" className="ml-3 block text-sm font-medium text-foreground">
                  Two
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="panels-three"
                  type="radio"
                  value="Three"
                  {...register('electricalPanels')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="panels-three" className="ml-3 block text-sm font-medium text-foreground">
                  Three
                </label>
              </div>
            </div>
            {errors.electricalPanels && (
              <p className="mt-1 text-sm text-destructive">{errors.electricalPanels.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Animals Present
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="animals-dogs"
                  type="radio"
                  value="Dogs"
                  {...register('animals')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="animals-dogs" className="ml-3 block text-sm font-medium text-foreground">
                  Dogs
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="animals-cats"
                  type="radio"
                  value="Cats"
                  {...register('animals')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="animals-cats" className="ml-3 block text-sm font-medium text-foreground">
                  Cats
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="animals-none"
                  type="radio"
                  value="None"
                  {...register('animals')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="animals-none" className="ml-3 block text-sm font-medium text-foreground">
                  None
                </label>
              </div>
            </div>
            {errors.animals && (
              <p className="mt-1 text-sm text-destructive">{errors.animals.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Does this project have a battery?
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="battery-yes"
                  type="radio"
                  value="Yes"
                  {...register('hasBattery')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="battery-yes" className="ml-3 block text-sm font-medium text-foreground">
                  Yes
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="battery-no"
                  type="radio"
                  value="No"
                  {...register('hasBattery')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="battery-no" className="ml-3 block text-sm font-medium text-foreground">
                  No
                </label>
              </div>
            </div>
            {errors.hasBattery && (
              <p className="mt-1 text-sm text-destructive">{errors.hasBattery.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Construction Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
        <h3 className="text-lg font-medium text-foreground">Construction</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Does this project require any construction or electrical work?
            </label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="construction-yes"
                  type="radio"
                  value="Yes"
                  {...register('requiresConstruction')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="construction-yes" className="ml-3 block text-sm font-medium text-foreground">
                  Yes
                </label>
              </div>
              <div className="flex items-center">
                <input
                  id="construction-no"
                  type="radio"
                  value="No"
                  {...register('requiresConstruction')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary border-border"
                />
                <label htmlFor="construction-no" className="ml-3 block text-sm font-medium text-foreground">
                  No
                </label>
              </div>
            </div>
            {errors.requiresConstruction && (
              <p className="mt-1 text-sm text-destructive">{errors.requiresConstruction.message}</p>
            )}
          </div>

          {requiresConstruction === 'Yes' && (
            <div className="sm:col-span-2">
              <label htmlFor="constructionDetails" className="block text-sm font-medium text-foreground">
                Please explain the construction or electrical work needed
              </label>
              <textarea
                id="constructionDetails"
                rows={3}
                {...register('constructionDetails')}
                className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
                placeholder="Describe the construction or electrical work needed..."
              />
              {errors.constructionDetails && (
                <p className="mt-1 text-sm text-destructive">{errors.constructionDetails.message}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Installer Note Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
        <h3 className="text-lg font-medium text-foreground">Installer Note</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label htmlFor="installerNote" className="block text-sm font-medium text-foreground">
              Note to Installer (From Sales Rep)
            </label>
            <textarea
              id="installerNote"
              rows={4}
              {...register('installerNote')}
              className="mt-1 block w-full rounded-md border border-border bg-white p-2 shadow-sm focus:border-primary-500 focus:ring-primary sm:text-sm"
              placeholder="Add any special instructions or notes for the installer..."
            />
            {errors.installerNote && (
              <p className="mt-1 text-sm text-destructive">{errors.installerNote.message}</p>
            )}
          </div>
        </div>
      </div>

      <div>
        <button
          type="submit"
          disabled={isLoading || isLoadingCiLevel}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primaryHover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
        >
          {isLoading ? 'Submitting...' : 'Book Appointment'}
        </button>
      </div>
    </form>
  );
}

export default BookingFormEnsite;